// src/belediye/components/NavbarBaslikContext.js
//
// FAZ-88 — DETAY SAYFASI → NAVBAR BAŞLIĞI KÖPRÜSÜ
//
// Navbar rotayı bilir ama KAYDI bilmez: "/yonetim/basvurular/4471" adresinden
// "Kaldırım çökmesi" başlığı çıkarılamaz. Detay sayfası bunu tek satırla
// bildirir; navbar orta yuvaya yazar, sol yuvayı geri okuna çevirir.
//
// Bu, silinen değil GİZLENEN yüzen geri düğmesinin mobildeki karşılığıdır:
// geri hareketi kaybolmuyor, sistem genelinde tek ve sabit bir noktaya
// (ekranın sol-üstü) taşınıyor.

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

const Ctx = createContext({ baslik: null, setBaslik: () => {} });

export function NavbarBaslikSaglayici({ children }) {
  const [baslik, setBaslikRaw] = useState(null);

  // Aynı değeri tekrar yazmak yeniden çizim tetiklemesin.
  // FAZ-80 dersi: DEĞİŞMEYEN bir dispatch sayfayı söküyordu (fotoğraf
  // seçilince sayfa "yenilendi" sanılıyordu). Kimlik alanları eşitse
  // eski nesne aynen döner → referans değişmez → çizim olmaz.
  // FAZ-91: kimlik alanları `ad` + `no` + `aktif`. Seçenek dizisi ve `onSec`
  // her çizimde yeniden kurulur; onları karşılaştırmaya çalışmak her render'da
  // yeni referans üretirdi.
  const setBaslik = useCallback((yeni) => {
    setBaslikRaw((eski) => {
      if (!eski && !yeni) return eski;
      if (
        eski &&
        yeni &&
        eski.ad === yeni.ad &&
        eski.no === yeni.no &&
        eski.tur === yeni.tur &&
        eski.aktif === yeni.aktif
      ) {
        // Aynı kayıt: eski referans korunur AMA geri çağrı tazelenmeli,
        // yoksa seçicinin onSec'i eski kapanışa (stale closure) takılır.
        return yeni.onSec && eski.onSec !== yeni.onSec
          ? { ...eski, onSec: yeni.onSec, secenekler: yeni.secenekler }
          : eski;
      }
      return yeni;
    });
  }, []);

  const deger = useMemo(() => ({ baslik, setBaslik }), [baslik, setBaslik]);
  return <Ctx.Provider value={deger}>{children}</Ctx.Provider>;
}

export function useNavbarBaslik() {
  return useContext(Ctx);
}

/**
 * Sayfaların çağıracağı kanca. Sayfadan çıkınca kendini temizler.
 *
 * @param {{ad: string, no?: string,
 *          secenekler?: Array<{key: string, label: string}>,
 *          aktif?: string, onSec?: Function}|null} deger
 *
 * `secenekler` verilirse orta yuva bir SEÇİCİ olur (FAZ-91 — Talepler
 * taraf seçimi); verilmezse düz metin kalır (FAZ-88 — detay başlığı).
 */
export function useNavbarBasligiAyarla(deger) {
  const { setBaslik } = useNavbarBaslik();

  // Nesne her çizimde yeniden kurulur; bağımlılığı ALANLARDAN türet.
  // Aksi hâlde effect her render'da koşar (yine FAZ-80 dersi).
  const ad = deger?.ad ?? null;
  const no = deger?.no ?? null;
  // FAZ-92 — "sayfa" mı "kayit" mı. Sol yuva yalnız KAYITTA geri okuna döner;
  // liste sayfasının adını yazmak geri oku çıkarmamalı.
  const tur = deger?.tur ?? "kayit";
  const aktif = deger?.aktif ?? null;
  const onSec = deger?.onSec ?? null;
  const secenekAnahtarlari = (deger?.secenekler || [])
    .map((s) => s.key)
    .join("|");

  useEffect(() => {
    if (!ad) {
      setBaslik(null);
      return undefined;
    }
    setBaslik({
      ad,
      no,
      tur,
      aktif,
      onSec,
      secenekler: deger?.secenekler || null,
    });
    return () => setBaslik(null);
    // `deger` bilerek bağımlılıkta değil: her çizimde yeni nesne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad, no, tur, aktif, onSec, secenekAnahtarlari, setBaslik]);
}
